<!--
  Outlook success illustration shown inside the wizard after a successful
  connection. Renders a stylised inbox preview.

  choice === "optionA" → folder mode  (folder chip overlay)
  choice === "optionB" → category mode (category colour dot overlay)
-->
<template>
  <div class="outlook-illus">
    <div class="inbox-preview">
      <div class="inbox-row unread">
        <span class="inbox-dot active" />
        <span class="inbox-avatar" style="background: #e8f0fe; color: #1a73e8"
          >SL</span
        >
        <span class="inbox-name">Sarah Lecomte</span>
        <span class="inbox-subject">Designs for v1.0 landing page</span>
        <span class="inbox-preview-text"> — Hey, here are the…</span>
      </div>
      <div class="inbox-row highlight">
        <span class="inbox-dot active" />
        <span class="inbox-avatar" style="background: #e6f4ea; color: #188038"
          >TN</span
        >
        <span class="inbox-name">Thomas Nguyen</span>
        <span class="inbox-subject">Re: Video shoot — location confirmed</span>
        <span class="inbox-preview-text"> — Perfect, the st…</span>
      </div>
      <div class="inbox-row unread">
        <span class="inbox-dot active" />
        <span class="inbox-avatar" style="background: #fce8e6; color: #c5221f"
          >AM</span
        >
        <span class="inbox-name">Alexandre Moreau</span>
        <span class="inbox-subject">Investor update — June</span>
        <span class="inbox-preview-text"> — Happy to share that MR…</span>
      </div>
      <div class="inbox-row">
        <span class="inbox-dot" />
        <span class="inbox-avatar" style="background: #f3e8fd; color: #8430ce"
          >CC</span
        >
        <span class="inbox-name">Clara Chevalier</span>
        <span class="inbox-subject">Freelance contract — signature needed</span>
        <span class="inbox-preview-text"> — Hi, I've attache…</span>
      </div>
      <div class="inbox-row">
        <span class="inbox-dot" />
        <span class="inbox-avatar" style="background: #fff3e0; color: #e65100"
          >LB</span
        >
        <span class="inbox-name">Lena Baumann</span>
        <span class="inbox-subject">Partnership proposal — Routine</span>
        <span class="inbox-preview-text"> — I work on the e…</span>
      </div>
    </div>

    <div v-if="choice === 'optionA'" class="inbox-overlay">
      <span>📁</span>
      <span>{{ filterName }}</span>
    </div>
    <div v-else-if="choice === 'optionB'" class="inbox-overlay">
      <span class="category-dot" />
      <span>{{ filterName }}</span>
    </div>
  </div>

  <p class="success-caption">
    <template v-if="choice === 'optionA'">
      Emails in folder <strong>{{ filterName }}</strong> will automatically
      become tasks in your Routine inbox.
    </template>
    <template v-else>
      Emails categorized as <strong>{{ filterName }}</strong> will automatically
      become tasks in your Routine inbox.
    </template>
  </p>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
  props: {
    choice: { type: String, required: true },
    filterName: { type: String, default: "" },
  },
});
</script>

<style scoped lang="scss">
@use "../../../styles/settings.scss";

.outlook-illus {
  flex: 1;
  border-radius: $border-radius-m;
  overflow: hidden;
  position: relative;
  min-height: 160px;
  @include themed() {
    border: 1px solid t($grey-10);
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    @include themed() {
      background: linear-gradient(to bottom, transparent 40%, t($bg) 100%);
    }
  }
}

.inbox-preview {
  display: flex;
  flex-direction: column;
  opacity: 0.78;
  pointer-events: none;
  user-select: none;
}

.outlook-illus:has(.inbox-overlay) .inbox-preview {
  opacity: 1;
}

.inbox-overlay {
  position: absolute;
  top: 80px;
  inset-inline-end: 14px;
  transform: translateY(-50%) translateY(-20px);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: $border-radius-m;
  @extend %font-caption-regular;
  pointer-events: none;
  animation: overlay-pulse 2s ease-out infinite;
  @include themed() {
    background: t($bg);
    color: t($grey-80);
    border: 1px solid t($grey-30);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  }
}

.category-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background: #0078d4;
}

@keyframes overlay-pulse {
  0% {
    box-shadow:
      0 0 0 0 rgba(0, 120, 212, 0.45),
      0 2px 8px rgba(0, 0, 0, 0.12);
  }
  60% {
    box-shadow:
      0 0 0 10px rgba(0, 120, 212, 0),
      0 2px 8px rgba(0, 0, 0, 0.12);
  }
  100% {
    box-shadow:
      0 0 0 0 rgba(0, 120, 212, 0),
      0 2px 8px rgba(0, 0, 0, 0.12);
  }
}

.inbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  height: 40px;
  font-size: 11px;
  @include themed() {
    border-bottom: 1px solid t($grey-05);
  }

  &.unread {
    @include themed() {
      background: t($grey-03);
    }

    .inbox-name,
    .inbox-subject {
      font-weight: 600;
    }
  }

  &.highlight {
    @include themed() {
      background: rgba(0, 120, 212, 0.06);
    }

    .inbox-name,
    .inbox-subject {
      font-weight: 600;
    }
  }
}

.inbox-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  &.active {
    background: #0078d4;
  }
}

.inbox-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  flex-shrink: 0;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.inbox-name {
  width: 110px;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  @include themed() {
    color: t($primary);
  }
}

.inbox-subject {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @include themed() {
    color: t($primary);
  }
}

.inbox-preview-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
  flex-shrink: 0;
  @include themed() {
    color: t($grey-50);
  }
}

.success-caption {
  @extend %font-body-regular;
  margin: 0;
  @include themed() {
    color: t($grey-50);
  }
}
</style>
